import { ImageDrop } from "quill-image-drop-module";
import ImageResize from "quill-image-resize-module-react";
import { type JSX } from "react";
import { Quill } from "react-quill";

interface QuillHistory {
  history: {
    undo: () => void;
    redo: () => void;
  };
}

const CustomUndo = (): JSX.Element => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
    <path
      className="ql-stroke"
      d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"
    />
  </svg>
);

const CustomRedo = (): JSX.Element => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
    <path
      className="ql-stroke"
      d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"
    />
  </svg>
);

function undoChange(this: { quill: QuillHistory }): void {
  const { quill } = this;
  quill.history.undo();
}

function redoChange(this: { quill: QuillHistory }): void {
  const { quill } = this;
  quill.history.redo();
}

if (typeof window !== "undefined" && window.Quill === undefined) {
  window.Quill = Quill;
}

const Size = Quill.import("formats/size");
Size.whitelist = ["small", "medium", "large"];
Quill.register(Size, true);

Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageDrop", ImageDrop);

export const modules = {
  imageDrop: true,
  imageResize: {
    modules: ["Resize", "DisplaySize"],
  },
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo: undoChange,
      redo: redoChange,
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

export const formats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "align",
  "strike",
  "script",
  "blockquote",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "color",
  "code-block",
];

export const QuillToolbar = (): JSX.Element => (
  <div id="toolbar" className="pb-2">
    <span>
      <button className="ql-undo">
        <CustomUndo />
      </button>
      <button className="ql-redo">
        <CustomRedo />
      </button>
    </span>
    <span>
      <select className="ql-size" title="Font Size" defaultValue="medium">
        <option value="small">Small</option>
        <option value="medium">Medium</option>
        <option value="large">Large</option>
      </select>
      <select className="ql-header" title="Heading Style" defaultValue="3">
        <option value="1">Heading</option>
        <option value="2">Subheading</option>
        <option value="3">Normal</option>
      </select>
    </span>
    <span>
      <button title="Bold" className="ql-bold" />
      <button title="Italic" className="ql-italic" />
      <button title="Underline" className="ql-underline" />
      <button title="Strikethrough" className="ql-strike" />
    </span>
    <span>
      <button title="Ordered List" className="ql-list" value="ordered" />
      <button title="Bulleted List" className="ql-list" value="bullet" />
      <button title="Indent Left" className="ql-indent" value="-1" />
      <button title="Indent Right" className="ql-indent" value="+1" />
    </span>
    <span>
      <button title="Superscript" className="ql-script" value="super" />
      <button title="Subscript" className="ql-script" value="sub" />
      <button title="Blockquote" className="ql-blockquote" />
    </span>
    <span>
      <select title="Text Align" className="ql-align" />
      <select title="Font Color" className="ql-color" />
      <select title="Highlight" className="ql-background" />
    </span>
    <span>
      <button title="Add Link" className="ql-link" />
      <button title="Add Image" className="ql-image" />
    </span>
    <span>
      <button title="Add Formula" className="ql-formula" />
      <button title="Add Codeblock" className="ql-code-block" />
      <button title="Clear Formatting" className="ql-clean" />
    </span>
  </div>
);

export default QuillToolbar;
